/**
 * utils/api.js — localStorage demo layer
 *
 * Replaces the backend API with localStorage persistence so the
 * app runs entirely in the browser with zero server dependencies.
 *
 * Storage keys:
 *   lifeos_user      → { id, name, email, avatar_color }
 *   lifeos_workouts  → Workout[]
 *   lifeos_sleep     → SleepLog[]
 *   lifeos_tasks     → Task[]
 *
 * Every exported function is async and returns the same shape the
 * real backend would, so all page components work unchanged.
 */

import { isAfter, isBefore, addDays, startOfWeek, endOfWeek } from 'date-fns';

// ─── Helpers ──────────────────────────────────────────────────────

/** Generate a simple unique ID (no UUID library needed) */
const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

/** Read a JSON array from localStorage, default to [] */
const load = (key) => {
  try { return JSON.parse(localStorage.getItem(key) || '[]'); }
  catch { return []; }
};

/** Write a value as JSON to localStorage */
const save = (key, value) => localStorage.setItem(key, JSON.stringify(value));

/** Simulate async network call */
const async_ = (value) => Promise.resolve(value);

// Avatar color palette (matches backend behaviour)
const AVATAR_COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#14b8a6', '#f59e0b', '#10b981'];
const randomColor = () => AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)];

// ─── AUTH API ─────────────────────────────────────────────────────

export const authAPI = {
  /**
   * Register: store user object in localStorage.
   * Throws if email already registered.
   */
  register: ({ name, email, password }) => {
    const existing = localStorage.getItem('lifeos_user');
    if (existing) {
      const u = JSON.parse(existing);
      if (u.email === email) throw new Error('Email already registered');
    }
    const user = { id: uid(), name, email, avatar_color: randomColor() };
    localStorage.setItem('lifeos_user', JSON.stringify(user));
    // Store password separately (demo only — never do this in production!)
    localStorage.setItem('lifeos_password', password);
    return async_({ user });
  },

  /**
   * Login: verify email + password match stored values.
   * Returns user object on success.
   */
  login: ({ email, password }) => {
    const stored = localStorage.getItem('lifeos_user');
    const storedPw = localStorage.getItem('lifeos_password');
    if (!stored) throw new Error('No account found. Please register first.');
    const user = JSON.parse(stored);
    if (user.email !== email || storedPw !== password) {
      throw new Error('Invalid credentials');
    }
    return async_({ user });
  },

  /** Return the current logged-in user (read from localStorage) */
  me: () => {
    const stored = localStorage.getItem('lifeos_user');
    if (!stored) throw new Error('Not authenticated');
    return async_({ user: JSON.parse(stored) });
  },
};

// ─── WORKOUTS API ─────────────────────────────────────────────────

const WORKOUTS_KEY = 'lifeos_workouts';

export const workoutsAPI = {
  list: ({ limit = 20, page = 1, category } = {}) => {
    let items = load(WORKOUTS_KEY);
    if (category) items = items.filter(w => w.category === category);
    // Sort newest first
    items.sort((a, b) => new Date(b.date) - new Date(a.date));
    const start = (page - 1) * limit;
    const paged = items.slice(start, start + Number(limit));
    // Attach exercise/set counts (mirrors backend response shape)
    const workouts = paged.map(w => ({
      ...w,
      exercise_count: (w.exercises || []).length,
      total_sets: (w.exercises || []).reduce((n, e) => n + (e.sets || []).length, 0),
    }));
    return async_({ workouts, total: items.length, page, limit });
  },

  get: (id) => {
    const w = load(WORKOUTS_KEY).find(w => w.id === id);
    if (!w) throw new Error('Workout not found');
    return async_(w);
  },

  stats: () => {
    const all = load(WORKOUTS_KEY);
    const now = new Date();
    const weekStart = startOfWeek(now);
    const weekEnd = endOfWeek(now);

    const thisWeek = all.filter(w => {
      const d = new Date(w.date + 'T12:00:00');
      return isAfter(d, weekStart) && isBefore(d, weekEnd);
    }).length;

    // By category
    const catMap = {};
    all.forEach(w => { if (w.category) catMap[w.category] = (catMap[w.category] || 0) + 1; });
    const by_category = Object.entries(catMap)
      .map(([category, count]) => ({ category, count }))
      .sort((a, b) => b.count - a.count);

    // Top exercises (by name frequency)
    const exMap = {};
    all.forEach(w => (w.exercises || []).forEach(e => {
      if (e.name) exMap[e.name] = (exMap[e.name] || 0) + 1;
    }));
    const top_exercises = Object.entries(exMap)
      .map(([name, times_performed]) => ({ name, times_performed }))
      .sort((a, b) => b.times_performed - a.times_performed)
      .slice(0, 5);

    // 30-day volume trend
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 30);
    const volume_trend = all
      .filter(w => new Date(w.date) >= cutoff)
      .map(w => ({
        date: w.date,
        exercises: (w.exercises || []).length,
        volume_lbs: (w.exercises || []).reduce((total, e) =>
          total + (e.sets || []).reduce((s, set) =>
            s + ((set.weight || 0) * (set.reps || 0)), 0), 0),
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    return async_({ this_week: thisWeek, total_workouts: all.length, by_category, top_exercises, volume_trend });
  },

  create: (data) => {
    const workouts = load(WORKOUTS_KEY);
    const now = new Date().toISOString();
    const workout = {
      ...data,
      id: uid(),
      created_at: now,
      updated_at: now,
      exercises: (data.exercises || []).map(e => ({
        ...e,
        id: uid(),
        sets: (e.sets || []).map((s, i) => ({ ...s, id: uid(), set_number: i + 1 })),
      })),
    };
    workouts.unshift(workout);
    save(WORKOUTS_KEY, workouts);
    return async_(workout);
  },

  update: (id, data) => {
    const workouts = load(WORKOUTS_KEY);
    const idx = workouts.findIndex(w => w.id === id);
    if (idx === -1) throw new Error('Workout not found');
    workouts[idx] = { ...workouts[idx], ...data, updated_at: new Date().toISOString() };
    save(WORKOUTS_KEY, workouts);
    return async_(workouts[idx]);
  },

  delete: (id) => {
    const workouts = load(WORKOUTS_KEY).filter(w => w.id !== id);
    save(WORKOUTS_KEY, workouts);
    return async_({ message: 'Deleted', id });
  },
};

// ─── SLEEP API ────────────────────────────────────────────────────

const SLEEP_KEY = 'lifeos_sleep';

/** Compute duration in minutes between two ISO strings */
const durMins = (start, end) => Math.round((new Date(end) - new Date(start)) / 60000);

export const sleepAPI = {
  list: ({ days = 30 } = {}) => {
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - Number(days));
    const logs = load(SLEEP_KEY)
      .filter(l => new Date(l.sleep_start) >= cutoff)
      .sort((a, b) => new Date(b.sleep_start) - new Date(a.sleep_start));
    return async_({ logs });
  },

  analytics: (days = 30) => {
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - Number(days));
    const logs = load(SLEEP_KEY).filter(l => new Date(l.sleep_start) >= cutoff);

    if (logs.length === 0) {
      return async_({ averages: { avg_duration_mins: 0, avg_quality: 0, total_logs: 0, avg_awakenings: 0 }, trend: [], quality_distribution: [], by_day_type: [] });
    }

    const avgDur = Math.round(logs.reduce((s, l) => s + l.duration_mins, 0) / logs.length);
    const qualLogs = logs.filter(l => l.quality);
    const avgQuality = qualLogs.length
      ? (qualLogs.reduce((s, l) => s + l.quality, 0) / qualLogs.length).toFixed(1)
      : null;
    const avgAwakenings = (logs.reduce((s, l) => s + (l.awakenings || 0), 0) / logs.length).toFixed(1);

    // Trend data (one row per log, sorted ascending)
    const trend = [...logs]
      .sort((a, b) => new Date(a.sleep_start) - new Date(b.sleep_start))
      .map(l => ({
        date: l.sleep_start.split('T')[0],
        duration_mins: l.duration_mins,
        quality: l.quality,
      }));

    // Quality distribution
    const qMap = {};
    qualLogs.forEach(l => { qMap[l.quality] = (qMap[l.quality] || 0) + 1; });
    const quality_distribution = Object.entries(qMap)
      .map(([quality, count]) => ({ quality: Number(quality), count }))
      .sort((a, b) => a.quality - b.quality);

    // Weekday vs weekend
    const weekday = logs.filter(l => ![0, 6].includes(new Date(l.sleep_start).getDay()));
    const weekend = logs.filter(l => [0, 6].includes(new Date(l.sleep_start).getDay()));
    const avg = (arr, key) => arr.length ? Math.round(arr.reduce((s, l) => s + l[key], 0) / arr.length) : 0;
    const by_day_type = [
      { day_type: 'weekday', avg_duration: avg(weekday, 'duration_mins'), avg_quality: avg(weekday, 'quality') },
      { day_type: 'weekend', avg_duration: avg(weekend, 'duration_mins'), avg_quality: avg(weekend, 'quality') },
    ];

    return async_({
      averages: { avg_duration_mins: avgDur, avg_quality: avgQuality, total_logs: logs.length, avg_awakenings: avgAwakenings },
      trend,
      quality_distribution,
      by_day_type,
    });
  },

  create: (data) => {
    const logs = load(SLEEP_KEY);
    const log = {
      ...data,
      id: uid(),
      duration_mins: durMins(data.sleep_start, data.sleep_end),
      awakenings: data.awakenings || 0,
      created_at: new Date().toISOString(),
    };
    logs.unshift(log);
    save(SLEEP_KEY, logs);
    return async_(log);
  },

  update: (id, data) => {
    const logs = load(SLEEP_KEY);
    const idx = logs.findIndex(l => l.id === id);
    if (idx === -1) throw new Error('Sleep log not found');
    const updated = {
      ...logs[idx], ...data,
      duration_mins: durMins(
        data.sleep_start || logs[idx].sleep_start,
        data.sleep_end   || logs[idx].sleep_end
      ),
    };
    logs[idx] = updated;
    save(SLEEP_KEY, logs);
    return async_(updated);
  },

  delete: (id) => {
    save(SLEEP_KEY, load(SLEEP_KEY).filter(l => l.id !== id));
    return async_({ message: 'Deleted', id });
  },
};

// ─── CALENDAR API ─────────────────────────────────────────────────

const TASKS_KEY = 'lifeos_tasks';

export const calendarAPI = {
  tasks: ({ month, completed, category, priority } = {}) => {
    let items = load(TASKS_KEY);

    if (month) {
      const [y, m] = month.split('-').map(Number);
      items = items.filter(t => {
        if (!t.due_date) return false;
        const d = new Date(t.due_date);
        return d.getFullYear() === y && d.getMonth() + 1 === m;
      });
    }
    if (completed !== undefined) items = items.filter(t => t.completed === (completed === 'true' || completed === true));
    if (category) items = items.filter(t => t.category === category);
    if (priority) items = items.filter(t => t.priority === priority);

    items.sort((a, b) => {
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return new Date(a.due_date) - new Date(b.due_date);
    });

    return async_({ tasks: items });
  },

  upcoming: (days = 7) => {
    const now = new Date();
    const limit = addDays(now, Number(days));
    const tasks = load(TASKS_KEY)
      .filter(t => !t.completed && t.due_date)
      .filter(t => { const d = new Date(t.due_date); return isAfter(d, now) && isBefore(d, limit); })
      .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
    return async_({ tasks });
  },

  stats: () => {
    const all = load(TASKS_KEY);
    const now = new Date();
    const weekAhead = addDays(now, 7);
    const pending   = all.filter(t => !t.completed).length;
    const completed = all.filter(t => t.completed).length;
    const overdue   = all.filter(t => !t.completed && t.due_date && isBefore(new Date(t.due_date), now)).length;
    const due_this_week = all.filter(t => !t.completed && t.due_date && isAfter(new Date(t.due_date), now) && isBefore(new Date(t.due_date), weekAhead)).length;

    const catMap = {};
    all.forEach(t => { if (t.category) catMap[t.category] = (catMap[t.category] || 0) + 1; });
    const by_category = Object.entries(catMap).map(([category, count]) => ({ category, count }));

    return async_({ summary: { pending, completed, overdue, due_this_week }, by_category });
  },

  createTask: (data) => {
    const tasks = load(TASKS_KEY);
    const task = { ...data, id: uid(), completed: false, created_at: new Date().toISOString() };
    tasks.push(task);
    save(TASKS_KEY, tasks);
    return async_(task);
  },

  updateTask: (id, data) => {
    const tasks = load(TASKS_KEY);
    const idx = tasks.findIndex(t => t.id === id);
    if (idx === -1) throw new Error('Task not found');
    tasks[idx] = {
      ...tasks[idx], ...data,
      completed_at: data.completed === true ? new Date().toISOString()
                  : data.completed === false ? null
                  : tasks[idx].completed_at,
    };
    save(TASKS_KEY, tasks);
    return async_(tasks[idx]);
  },

  deleteTask: (id) => {
    save(TASKS_KEY, load(TASKS_KEY).filter(t => t.id !== id));
    return async_({ message: 'Deleted', id });
  },
};
